import React, { useState, useEffect, useRef } from 'react';
import '../styles/NewsDashboard.css';

// Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const NewsDashboard = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredNews, setHoveredNews] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [sortConfig] = useState({ key: 'created_date', direction: 'desc' });
  const [filters, setFilters] = useState({ creator: '' });
  const [pagination, setPagination] = useState({ total: 0, limit: 100, offset: 0, pages: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Normalize sentiment to a value between -1 and 1
  const normalizeSentiment = (sentiment) => {
    if (typeof sentiment === 'number') {
      // Assuming sentiment is already in range -1 to 1
      return Math.max(-1, Math.min(1, sentiment));
    }
    
    // If sentiment is a string, convert to numeric
    const sentimentLower = String(sentiment).toLowerCase();
    if (sentimentLower.includes('positive') || sentimentLower.includes('bullish')) {
      return 0.7;
    } else if (sentimentLower.includes('negative') || sentimentLower.includes('bearish')) {
      return -0.7;
    } else if (sentimentLower.includes('neutral')) {
      return 0;
    }
    
    // Try to parse as number
    const parsed = parseFloat(sentiment);
    return isNaN(parsed) ? 0 : Math.max(-1, Math.min(1, parsed));
  };

  // Get color based on sentiment
  const getSentimentColor = (sentiment) => {
    const normalized = normalizeSentiment(sentiment);
    
    if (normalized > 0.3) {
      return '#10b981a2'; // Green - Positive
    } else if (normalized < -0.3) {
      return '#ef444467'; // Red - Negative
    } else {
      return '#71717a'; // Gray - Neutral
    }
  };

  // Get sentiment label
  const getSentimentLabel = (sentiment) => {
    const normalized = normalizeSentiment(sentiment);
    
    if (normalized > 0.3) {
      return 'Positive';
    } else if (normalized < -0.3) {
      return 'Negative';
    } else {
      return 'Neutral';
    }
  };

  // Helper for deterministic randomness based on seed
  const getPseudoRandom = (seed) => {
    let hash = 0;
    const str = String(seed);
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    // Normalize to 0-1
    return (Math.abs(hash) % 1000) / 1000;
  };

  // Calculate statistics
  const stats = React.useMemo(() => {
    if (!data.length) return null;
    
    let totalSentiment = 0;
    let bullish = 0;
    let bearish = 0;
    let neutral = 0;
    
    data.forEach(item => {
      const normalized = normalizeSentiment(item.sentiment);
      totalSentiment += normalized;
      
      if (normalized > 0.3) bullish++;
      else if (normalized < -0.3) bearish++;
      else neutral++;
    });
    
    return {
      avgSentiment: totalSentiment / data.length,
      bullish,
      bearish,
      neutral,
      total: data.length
    };
  }, [data]);

  const fetchData = async (signal) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        sort: sortConfig.key,
        order: sortConfig.direction,
        limit: pagination.limit,
        offset: pagination.offset
      });

      if (filters.creator) params.append('creator', filters.creator);

      const response = await fetch(`${API_BASE_URL}/api/data/news?${params}`, {
        credentials: 'include',
        signal
      });

      const result = await response.json();

      if (!isMountedRef.current) return;

      if (result.success) {
        setData(result.data);
        setPagination(result.pagination);
        setError(null);
      } else {
        setError(result.message || 'Failed to fetch data');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (!isMountedRef.current) return;
      setError('Network error. Please try again.');
      console.error('Error fetching news data:', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [sortConfig, filters, pagination.offset, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({ ...prev, offset: newOffset }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading && data.length === 0) {
    return (
      <div className="news-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading News Intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="news-dashboard">
      <div className="news-header">
        <div className="header-content">
          <h2>📰 News </h2>
          <p className="news-subtitle">
            Real-time market sentiment analysis • {pagination.total} articles tracked
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="news-filters">
        <div className="filter-group">
          <label>Filter by Source:</label>
          <input
            type="text"
            placeholder="Search by creator/source..."
            value={filters.creator}
            onChange={(e) => handleFilterChange('creator', e.target.value)}
          />
        </div>
        <button 
          className="filter-clear"
          onClick={() => setFilters({ creator: '' })}
        >
          Clear Filters
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button onClick={fetchData}>Retry</button>
        </div>
      )}

      {data.length === 0 && !loading ? (
        <div className="no-data">
          <p>📰 No news data available</p>
          <p>News intelligence will appear here once data is collected.</p>
        </div>
      ) : (
        <>
          <div className="sentiment-section">
            {/* Column A: Square Heatmap */}
            <div className="heatmap-column">
              <div className="sentiment-heatmap-container glass-panel">
                <div className="heatmap-header">
                  <h3>Sentiment Distribution</h3>
                  <div className="sentiment-legend">
                    <span className="legend-item">
                      <span className="legend-dot negative"></span> Bearish
                    </span>
                    <span className="legend-item">
                      <span className="legend-dot neutral"></span> Neutral
                    </span>
                    <span className="legend-item">
                      <span className="legend-dot positive"></span> Bullish
                    </span>
                  </div>
                </div>

                <div className="heatmap-gradient-container square-aspect">
                  {/* Gradient bar */}
                  <div className="heatmap-gradient">
                    {/* Sentiment dots */}
                    <div className="sentiment-dots">
                      {data.map((item, index) => {
                        const normalized = normalizeSentiment(item.sentiment);
                        
                        // Generate deterministic random values based on ID
                        const seed = item.news_id || index;
                        const randX = getPseudoRandom(seed); // 0 to 1
                        const randY = getPseudoRandom(seed + '_y'); // 0 to 1
                        
                        // Base position: -1 is at 0%, 0 is at 50%, 1 is at 100%
                        let leftPos = ((normalized + 1) / 2) * 100;
                        
                        // Increased horizontal jitter: +/- 5% (was 3%)
                        const jitterX = (randX - 0.5) * 10;
                        leftPos = Math.max(5, Math.min(95, leftPos + jitterX));
                        
                        // Vertical position: Spread across 5-95% height randomly
                        // Increased spacing on Y axis
                        const topPos = 5 + (randY * 90);
                        
                        return (
                          <div
                            key={item.news_id || index}
                            className={`sentiment-dot ${hoveredNews?.news_id === item.news_id ? 'hovered' : ''} ${selectedNews?.news_id === item.news_id ? 'selected' : ''}`}
                            style={{
                              left: `${leftPos}%`,
                              backgroundColor: getSentimentColor(item.sentiment),
                              top: `${topPos}%`
                            }}
                            onMouseEnter={(e) => {
                              setHoveredNews(item);
                              const tooltipWidth = 400; // max-width from CSS
                              const tooltipHeight = 250; // estimated height
                              const padding = 15;
                              
                              // Calculate position, keeping tooltip within viewport
                              let x = e.clientX + padding;
                              let y = e.clientY + padding;
                              
                              // Check right edge
                              if (x + tooltipWidth > window.innerWidth) {
                                x = e.clientX - tooltipWidth - padding;
                              }
                              
                              // Check bottom edge
                              if (y + tooltipHeight > window.innerHeight) {
                                y = e.clientY - tooltipHeight - padding;
                              }
                              
                              // Check left edge
                              if (x < padding) {
                                x = padding;
                              }
                              
                              // Check top edge
                              if (y < padding) {
                                y = padding;
                              }
                              
                              setTooltipPosition({ x, y });
                            }}
                            onMouseMove={(e) => {
                              const tooltipWidth = 400;
                              const tooltipHeight = 250;
                              const padding = 15;
                              
                              let x = e.clientX + padding;
                              let y = e.clientY + padding;
                              
                              if (x + tooltipWidth > window.innerWidth) {
                                x = e.clientX - tooltipWidth - padding;
                              }
                              
                              if (y + tooltipHeight > window.innerHeight) {
                                y = e.clientY - tooltipHeight - padding;
                              }
                              
                              if (x < padding) {
                                x = padding;
                              }
                              
                              if (y < padding) {
                                y = padding;
                              }
                              
                              setTooltipPosition({ x, y });
                            }}
                            onMouseLeave={() => setHoveredNews(null)}
                            onClick={() => {
                              if (item.url) {
                                window.open(item.url, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            title={item.title}
                          >
                            <div className="dot-pulse"></div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Labels */}
                    <div className="heatmap-labels">
                      <span className="label-negative">Bearish</span>
                      <span className="label-neutral">Neutral</span>
                      <span className="label-positive">Bullish</span>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Column B: News Statistics */}
            {/* Tooltip rendered at a high level for proper layering */}
            {hoveredNews && (
              <div 
                className="heatmap-tooltip"
                style={{
                  position: 'fixed',
                  left: `${tooltipPosition.x}px`,
                  top: `${tooltipPosition.y}px`,
                  transform: 'none',
                  zIndex: 9999
                }}
                onClick={() => {
                  if (hoveredNews.url) {
                    window.open(hoveredNews.url, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                <h4>{hoveredNews.title}</h4>
                <p className="tooltip-meta">
                  <span className="tooltip-source">{hoveredNews.creator}</span>
                  <span className="tooltip-date">{formatDate(hoveredNews.created_date)}</span>
                </p>
                <p className="tooltip-sentiment">
                  Sentiment: <strong style={{ color: getSentimentColor(hoveredNews.sentiment) }}>
                    {getSentimentLabel(hoveredNews.sentiment)}
                  </strong>
                </p>
                {hoveredNews.url && (
                  <p className="tooltip-click-hint">
                    🖱️ Click to read article
                  </p>
                )}
              </div>
            )}

            <div className="stats-column">
              <div className="news-statistics-card glass-panel">
                <div className="stats-header">
                  <h3>Market Intelligence</h3>
                </div>
                
                {stats && (
                  <div className="stats-content">
                    <div className="stat-item main-stat">
                      <span className="stat-label">Average Sentiment</span>
                      <div className="stat-value-container">
                        <span 
                          className="stat-value"
                          style={{ color: getSentimentColor(stats.avgSentiment) }}
                        >
                          {stats.avgSentiment > 0 ? '+' : ''}{stats.avgSentiment.toFixed(2)}
                        </span>
                        <span className="stat-sublabel">
                          {getSentimentLabel(stats.avgSentiment)}
                        </span>
                      </div>
                    </div>

                    <div className="stat-grid">
                      <div className="stat-item">
                        <span className="stat-label">Total Articles</span>
                        <span className="stat-value">{stats.total}</span>
                      </div>
                      
                      <div className="stat-item">
                        <span className="stat-label">Local Time</span>
                        <div className="stat-value date-time-display">
                          <span className="time">{currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</span>
                          <span className="date">{currentTime.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        </div>
                      </div>

                      <div className="stat-item">
                        <span className="stat-label">Bullish</span>
                        <span className="stat-value positive">{stats.bullish}</span>
                      </div>

                      <div className="stat-item">
                        <span className="stat-label">Bearish</span>
                        <span className="stat-value negative">{stats.bearish}</span>
                      </div>
                    </div>

                    <div className="stat-distribution-bar">
                      <div 
                        className="dist-segment negative" 
                        style={{ width: `${(stats.bearish / stats.total) * 100}%` }}
                        title={`Bearish: ${stats.bearish}`}
                      ></div>
                      <div 
                        className="dist-segment neutral" 
                        style={{ width: `${(stats.neutral / stats.total) * 100}%` }}
                        title={`Neutral: ${stats.neutral}`}
                      ></div>
                      <div 
                        className="dist-segment positive" 
                        style={{ width: `${(stats.bullish / stats.total) * 100}%` }}
                        title={`Bullish: ${stats.bullish}`}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* News List */}
          <div className="news-list-container">
            <div className="news-list-header">
              <h3>News Articles ({data.length})</h3>
            </div>

            <div className="news-list">
              {data.map((item) => (
                <div 
                  key={item.news_id}
                  className={`news-item ${selectedNews?.news_id === item.news_id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedNews(item);
                    if (item.url) {
                      window.open(item.url, '_blank', 'noopener,noreferrer');
                    }
                  }}
                >
                  <div 
                    className="news-item-border" 
                    style={{ backgroundColor: getSentimentColor(item.sentiment) }}
                  ></div>
                  
                  <div className="news-item-content">
                    <div className="news-item-header">
                      <h4 className="news-title">{item.title}</h4>
                      <div className="news-item-badges">
                        <span 
                          className="sentiment-badge"
                          style={{ 
                            backgroundColor: getSentimentColor(item.sentiment),
                            color: 'white'
                          }}
                        >
                          {getSentimentLabel(item.sentiment)}
                        </span>
                        <span className="news-date">
                          🕐 {formatDate(item.created_date)}
                        </span>
                      </div>
                    </div>

                    {item.url && (
                      <a 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="news-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Read Article →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} articles
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => handlePageChange(0)}
                  disabled={pagination.offset === 0}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(pagination.offset - pagination.limit)}
                  disabled={pagination.offset === 0}
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {Math.floor(pagination.offset / pagination.limit) + 1} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange((pagination.pages - 1) * pagination.limit)}
                  disabled={pagination.offset + pagination.limit >= pagination.total}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {loading && data.length > 0 && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default NewsDashboard;
